import { ReactNode, useEffect, useState } from "react";

import {
    usePopover,
    PopoverWrapper,
    SPACING,
    Row,
    Icon,
    IconButton,
    Button,
    Column,
    IconType,
    filterButtonRippleSx,
    useAppPalette,
    BORDER_RADIUS_ROUNDED
} from "@bridgesplit/ui";
import { FilterConfig, FilterProps, isFilterEqual } from "@bridgesplit/abf-react";

import { PopoverContentSection } from "./common";

type TableFilterPopoverProps<C extends FilterConfig> = {
    children: ReactNode;
    popover: ReturnType<typeof usePopover>;
    applyDisabled: boolean;
    onApply: () => void;
    onOpen?: () => void;
    filterProps: FilterProps<C>;
    filterKeys: (keyof C)[];
    buttonLabel: string;
    buttonIcon?: IconType;
};

export default function TableFilterPopover<C extends FilterConfig>({
    children,
    popover,
    applyDisabled,
    onApply,
    onOpen,
    filterProps,
    filterKeys,
    buttonLabel,
    buttonIcon = "filter"
}: TableFilterPopoverProps<C>) {
    const { filters, defaultState, setFilters } = filterProps;

    // Update button for change
    const [filterChanged, setFilterChanged] = useState(false);
    const [prevFilters, setPrevFilters] = useState(filters);

    useEffect(() => {
        setFilterChanged(filterKeys.map((key) => isFilterEqual(filters[key], prevFilters[key])).some((v) => !v));
        setPrevFilters(filters);
    }, [filterKeys, filters, prevFilters]);

    const isActive = filterKeys.map((key) => isFilterEqual(filters[key], defaultState[key])).some((v) => !v);

    const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
        onOpen?.();
        popover.open(e);
    };

    const handleClear = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        const updated = { ...filters };
        for (const key of filterKeys) {
            updated[key] = defaultState[key];
        }
        setFilters((prev) => ({
            ...prev,
            ...updated
        }));
        popover.close();
    };

    return (
        <>
            <PopoverWrapper transformOrigin={{ vertical: -1 * SPACING, horizontal: SPACING * 2 }} {...popover}>
                <Column sx={{ width: "250px" }} spacing={0}>
                    {children}
                    <PopoverFooter disabled={applyDisabled} onApply={onApply} onCancel={popover.close} />
                </Column>
            </PopoverWrapper>
            <FilterButton
                isActive={isActive}
                showUpdate={!filterChanged}
                onOpen={handleOpen}
                onClear={handleClear}
                label={buttonLabel}
                icon={buttonIcon}
            />
        </>
    );
}

export function PopoverFooter({
    onApply,
    onCancel,
    disabled = false
}: {
    onApply: () => void;
    onCancel: () => void;
    disabled?: boolean;
}) {
    return (
        <PopoverContentSection dividerTop={true} dividerBottom={false}>
            <Row spacing={1}>
                <Button variant="outlined" onClick={onCancel} fullWidth height={35} textProps={{ variant: "body2" }}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    disabled={disabled}
                    onClick={onApply}
                    height={35}
                    fullWidth
                    textProps={{ variant: "body2" }}
                >
                    Apply
                </Button>
            </Row>
        </PopoverContentSection>
    );
}

export type FilterButtonProps = {
    isActive: boolean;
    onOpen: (event: React.MouseEvent<HTMLElement>) => void;
    onClear: (event: React.MouseEvent<HTMLElement>) => void;
    showUpdate?: boolean;
    label: string;
    icon?: IconType;
};

export function FilterButton({ isActive, onOpen, onClear, showUpdate, label, icon }: FilterButtonProps) {
    const { divider, secondaryBackground, secondaryHoverBackground, secondary, textSecondary } = useAppPalette();
    const updateSx = isActive && showUpdate;
    const textColor = isActive ? secondary : textSecondary;

    const buttonSx = {
        border: `1px solid ${isActive ? secondary : divider}`,
        borderRadius: BORDER_RADIUS_ROUNDED,
        px: 1.5,
        fontWeight: 400,
        background: `${
            isActive ? secondaryHoverBackground : "transparent"
        } radial-gradient(circle, ${secondaryHoverBackground} 1%, transparent 1%) center/100%`,
        backgroundPosition: "center",
        transition: "background border 0.2s ease",
        "&:hover": {
            backgroundColor: isActive ? secondaryBackground : divider,
            border: `1px solid ${isActive ? secondary : divider}`,
            transition: "background border 0.2s ease"
        },
        "& .MuiTouchRipple-child": {
            backgroundColor: isActive ? secondaryBackground : divider
        },
        alignItems: "center",
        ...(updateSx && filterButtonRippleSx)
    };

    return (
        <Button
            variant="outlined"
            size="small"
            component="div"
            onClick={onOpen}
            textProps={{
                variant: "body1",
                sx: { fontWeight: "normal", color: textColor }
            }}
            sx={buttonSx}
        >
            <Icon type={icon ?? "filter"} sx={{ color: textColor }} />
            {label}
            {isActive && (
                <IconButton
                    onClick={onClear}
                    background="never"
                    border={false}
                    icon="reject"
                    textColor="secondary"
                    textVariant="body1"
                    sx={{ padding: 0, fontWeight: "normal" }}
                />
            )}
        </Button>
    );
}
